import theme from "@shared/utils/themeColors";
import React from "react";
import { FaTimes } from "react-icons/fa";

interface MathSymbolKeyboardProps {
  onSelectSymbol: (symbol: string) => void;
  onClose: () => void;
}

/**
 * A keyboard component for inserting mathematical symbols
 */
export const MathSymbolKeyboard: React.FC<MathSymbolKeyboardProps> = ({
  onSelectSymbol,
  onClose,
}) => {
  // Define groups of mathematical symbols
  const symbolGroups = [
    {
      name: "Operators",
      symbols: [
        { symbol: "±", description: "Plus-minus" },
        { symbol: "×", description: "Multiplication" },
        { symbol: "÷", description: "Division" },
        { symbol: "∑", description: "Summation" },
        { symbol: "∏", description: "Product" },
        { symbol: "√", description: "Square root" },
        { symbol: "∛", description: "Cube root" },
        { symbol: "∜", description: "Fourth root" },
      ],
    },
    {
      name: "Superscripts",
      symbols: [
        { symbol: "⁰", description: "Superscript 0" },
        { symbol: "¹", description: "Superscript 1" },
        { symbol: "²", description: "Squared" },
        { symbol: "³", description: "Cubed" },
        { symbol: "⁴", description: "To the power of 4" },
        { symbol: "⁵", description: "To the power of 5" },
        { symbol: "⁶", description: "To the power of 6" },
        { symbol: "⁷", description: "To the power of 7" },
        { symbol: "⁸", description: "To the power of 8" },
        { symbol: "⁹", description: "To the power of 9" },
        { symbol: "⁺", description: "Superscript plus" },
        { symbol: "⁻", description: "Superscript minus" },
        { symbol: "⁼", description: "Superscript equals" },
        { symbol: "⁽", description: "Superscript left parenthesis" },
        { symbol: "⁾", description: "Superscript right parenthesis" },
      ],
    },
    {
      name: "Subscripts",
      symbols: [
        { symbol: "₀", description: "Subscript 0" },
        { symbol: "₁", description: "Subscript 1" },
        { symbol: "₂", description: "Subscript 2" },
        { symbol: "₃", description: "Subscript 3" },
        { symbol: "₄", description: "Subscript 4" },
        { symbol: "₅", description: "Subscript 5" },
        { symbol: "₆", description: "Subscript 6" },
        { symbol: "₇", description: "Subscript 7" },
        { symbol: "₈", description: "Subscript 8" },
        { symbol: "₉", description: "Subscript 9" },
        { symbol: "₊", description: "Subscript plus" },
        { symbol: "₋", description: "Subscript minus" },
        { symbol: "₌", description: "Subscript equals" },
      ],
    },
    {
      name: "Comparison",
      symbols: [
        { symbol: "≠", description: "Not equal" },
        { symbol: "≈", description: "Approximately equal" },
        { symbol: "≤", description: "Less than or equal" },
        { symbol: "≥", description: "Greater than or equal" },
        { symbol: "≪", description: "Much less than" },
        { symbol: "≫", description: "Much greater than" },
      ],
    },
    {
      name: "Fractions",
      symbols: [
        { symbol: "½", description: "One half" },
        { symbol: "⅓", description: "One third" },
        { symbol: "¼", description: "One quarter" },
        { symbol: "⅕", description: "One fifth" },
        { symbol: "⅙", description: "One sixth" },
        { symbol: "⅛", description: "One eighth" },
      ],
    },
    {
      name: "Greek Letters",
      symbols: [
        { symbol: "α", description: "Alpha" },
        { symbol: "β", description: "Beta" },
        { symbol: "γ", description: "Gamma" },
        { symbol: "δ", description: "Delta" },
        { symbol: "ε", description: "Epsilon" },
        { symbol: "θ", description: "Theta" },
        { symbol: "λ", description: "Lambda" },
        { symbol: "μ", description: "Mu" },
        { symbol: "π", description: "Pi" },
        { symbol: "ρ", description: "Rho" },
        { symbol: "σ", description: "Sigma" },
        { symbol: "τ", description: "Tau" },
        { symbol: "φ", description: "Phi" },
        { symbol: "ω", description: "Omega" },
        { symbol: "Δ", description: "Capital Delta" },
        { symbol: "Π", description: "Capital Pi" },
        { symbol: "Σ", description: "Capital Sigma" },
        { symbol: "Ω", description: "Capital Omega" },
      ],
    },
    {
      name: "Others",
      symbols: [
        { symbol: "ƒ", description: "Function" },
        { symbol: "∞", description: "Infinity" },
        { symbol: "∝", description: "Proportional to" },
        { symbol: "∠", description: "Angle" },
        { symbol: "∡", description: "Measured angle" },
        { symbol: "∫", description: "Integral" },
        { symbol: "∬", description: "Double integral" },
        { symbol: "∭", description: "Triple integral" },
        { symbol: "∮", description: "Contour integral" },
        { symbol: "∂", description: "Partial derivative" },
        { symbol: "∇", description: "Nabla/Del" },
        { symbol: "∈", description: "Element of" },
        { symbol: "∉", description: "Not an element of" },
        { symbol: "∩", description: "Intersection" },
        { symbol: "∪", description: "Union" },
        { symbol: "⊂", description: "Subset of" },
        { symbol: "⊃", description: "Superset of" },
        { symbol: "⊆", description: "Subset of or equal to" },
        { symbol: "⊇", description: "Superset of or equal to" },
      ],
    },
    {
      name: "Additional Symbols",
      symbols: [
        { symbol: "°", description: "Degree" },
        { symbol: "′", description: "Prime" },
        { symbol: "″", description: "Double prime" },
        { symbol: "‴", description: "Triple prime" },
        { symbol: "℃", description: "Degree Celsius" },
        { symbol: "℉", description: "Degree Fahrenheit" },
        { symbol: "Å", description: "Angstrom" },
        { symbol: "ℵ", description: "Alef symbol" },
        { symbol: "∀", description: "For all" },
        { symbol: "∃", description: "There exists" },
        { symbol: "∄", description: "There does not exist" },
        { symbol: "∅", description: "Empty set" },
        { symbol: "≡", description: "Identical to" },
        { symbol: "≢", description: "Not identical to" },
        { symbol: "≤", description: "Less than or equal to" },
        { symbol: "≥", description: "Greater than or equal to" },
        { symbol: "≮", description: "Not less than" },
        { symbol: "≯", description: "Not greater than" },
      ],
    },
  ];

  return (
    <div
      className={`absolute z-10 mt-1 w-full ${theme.bg.primary} border border-gray-300 dark:border-gray-600 rounded-md shadow-lg`}
    >
      <div
        className={`flex justify-between items-center p-2 border-b ${theme.border.secondary}`}
      >
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Mathematical Symbols
        </h3>
        <button type="button" onClick={onClose} className={theme.text.muted}>
          <FaTimes size={16} />
        </button>
      </div>

      <div className="max-h-60 overflow-y-auto p-2">
        {symbolGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="mb-3">
            <h4 className={`text-xs font-semibold ${theme.text.muted}`}>
              {group.name}
            </h4>
            <div className="grid grid-cols-6 gap-1">
              {group.symbols.map((item, symbolIndex) => (
                <button
                  key={symbolIndex}
                  type="button"
                  onClick={() => onSelectSymbol(item.symbol)}
                  title={item.description}
                  className={`flex items-center justify-center h-8 border ${theme.border.secondary} rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200`}
                >
                  {item.symbol}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
